import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { X, User, Mail, Phone, Shield, Lock, Eye, EyeOff } from 'lucide-react';
import { authAPI, usersAPI } from '@shared/apiClient';
import toast from 'react-hot-toast';

export default function UserFormModal({ user, isOpen, onClose, mode = 'create' }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    role: 'technician',
    phone: '',
    isActive: true,
    capabilities: ['inspection', 'service'],
    coolingCapabilities: ['air', 'water'],
    includeInAutoSchedule: true,
    dailyCapacityUnits: '',
    dailyCapacityMinutes: '',
    homeAddress: '',
    startFromHomeAddress: false,
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // Initialize form data when user changes
  useEffect(() => {
    if (user && mode === 'edit') {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        username: user.username || '',
        email: user.email || '',
        password: '',
        role: user.role || 'technician',
        phone: user.phone || '',
        isActive: user.isActive !== undefined ? user.isActive : true,
        capabilities: user.capabilities || ['inspection'],
        coolingCapabilities: user.coolingCapabilities || ['air', 'water'],
        includeInAutoSchedule: user.includeInAutoSchedule ?? false,
        dailyCapacityUnits: user.dailyCapacityUnits ?? '',
        dailyCapacityMinutes: user.dailyCapacityMinutes ?? '',
        homeAddress: user.homeAddress ?? '',
        startFromHomeAddress: user.startFromHomeAddress ?? false,
      });
    } else {
      // Reset form for create mode
      setFormData({
        firstName: '',
        lastName: '',
        username: '',
        email: '',
        password: '',
        role: 'technician',
        phone: '',
        isActive: true,
        capabilities: ['inspection', 'service'],
        coolingCapabilities: ['air', 'water'],
        includeInAutoSchedule: true,
        dailyCapacityUnits: '',
        dailyCapacityMinutes: '',
        homeAddress: '',
        startFromHomeAddress: false,
      });
    }
    setErrors({});
    setShowPassword(false);
  }, [user, mode, isOpen]);

  // Create mutation (uses auth/register endpoint)
  const createMutation = useMutation(
    (data) => authAPI.register(data),
    {
      onSuccess: () => {
        toast.success('User created successfully!');
        queryClient.invalidateQueries('users');
        onClose();
      },
      onError: (error) => {
        const errorData = error.response?.data;
        if (errorData?.errors) {
          const fieldErrors = {};
          errorData.errors.forEach(err => {
            if (err.path) {
              fieldErrors[err.path] = err.msg;
            }
          });
          setErrors(fieldErrors);
        } else {
          toast.error(errorData?.message || 'Failed to create user');
        }
      }
    }
  );

  // Update mutation
  const updateMutation = useMutation(
    (data) => usersAPI.updateUser(user.id, data),
    {
      onSuccess: () => {
        toast.success('User updated successfully!');
        queryClient.invalidateQueries('users');
        queryClient.invalidateQueries(['users', user.id]);
        onClose();
      },
      onError: (error) => {
        const errorData = error.response?.data;
        if (errorData?.errors) {
          const fieldErrors = {};
          errorData.errors.forEach(err => {
            if (err.path) {
              fieldErrors[err.path] = err.msg;
            }
          });
          setErrors(fieldErrors);
        } else {
          toast.error(errorData?.message || 'Failed to update user');
        }
      }
    }
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleCapabilityToggle = (capability) => {
    setFormData((prev) => {
      const caps = prev.capabilities.includes(capability)
        ? prev.capabilities.filter((c) => c !== capability)
        : [...prev.capabilities, capability];
      return { ...prev, capabilities: caps.length > 0 ? caps : ['inspection'] };
    });
  };

  // Never let the last cooling type be unchecked — a tech servicing neither
  // could not be auto-scheduled at all.
  const handleCoolingToggle = (cooling) => {
    setFormData((prev) => {
      const caps = prev.coolingCapabilities.includes(cooling)
        ? prev.coolingCapabilities.filter((c) => c !== cooling)
        : [...prev.coolingCapabilities, cooling];
      return {
        ...prev,
        coolingCapabilities: caps.length > 0 ? caps : [cooling === 'air' ? 'water' : 'air'],
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    // Prepare data - only include password if it's provided (for create, it's required)
    const submitData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      username: formData.username.trim(),
      role: formData.role,
      phone: formData.phone.trim() || undefined
    };

    // Email is optional
    if (formData.email.trim()) {
      submitData.email = formData.email.trim();
    }

    // For create mode, password is required
    if (mode === 'create') {
      if (!formData.password || formData.password.length < 6) {
        setErrors({ password: 'Password must be at least 6 characters' });
        return;
      }
      submitData.password = formData.password;
    }

    // For edit mode, include isActive
    if (mode === 'edit') {
      submitData.isActive = formData.isActive;
    }

    if (formData.role === 'technician') {
      submitData.capabilities = formData.capabilities;
      submitData.coolingCapabilities = formData.coolingCapabilities;
      submitData.includeInAutoSchedule = formData.includeInAutoSchedule;
      submitData.dailyCapacityUnits = formData.dailyCapacityUnits === '' || formData.dailyCapacityUnits == null
        ? null
        : Number(formData.dailyCapacityUnits);
      submitData.dailyCapacityMinutes = formData.dailyCapacityMinutes === '' || formData.dailyCapacityMinutes == null
        ? null
        : Number(formData.dailyCapacityMinutes);
      submitData.homeAddress = formData.homeAddress.trim() || null;
      submitData.startFromHomeAddress = formData.startFromHomeAddress;
    }

    if (mode === 'create') {
      createMutation.mutate(submitData);
    } else {
      updateMutation.mutate(submitData);
    }
  };

  if (!isOpen) return null;

  const isLoading = createMutation.isLoading || updateMutation.isLoading;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="bg-gray-50 px-4 py-5 sm:px-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  {mode === 'create' ? 'Create New User' : 'Edit User'}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="px-4 py-5 sm:p-6 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 gap-6">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="inline h-4 w-4 mr-1" />
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.firstName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.lastName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                  )}
                </div>

                {/* Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="inline h-4 w-4 mr-1" />
                    Username *
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                    minLength={3}
                    maxLength={30}
                    pattern="[a-zA-Z0-9_]+"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.username ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="username"
                    disabled={mode === 'edit'} // Username cannot be changed after creation
                  />
                  {errors.username && (
                    <p className="mt-1 text-sm text-red-600">{errors.username}</p>
                  )}
                  {mode === 'create' && (
                    <p className="mt-1 text-xs text-gray-500">3-30 characters, letters, numbers, and underscores only</p>
                  )}
                  {mode === 'edit' && (
                    <p className="mt-1 text-xs text-gray-500">Username cannot be changed</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="inline h-4 w-4 mr-1" />
                    Email (Optional)
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="email@example.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">Optional - for contact purposes</p>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Lock className="inline h-4 w-4 mr-1" />
                    Password {mode === 'create' ? '*' : '(leave blank to keep current)'}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required={mode === 'create'}
                      minLength={mode === 'create' ? 6 : undefined}
                      className={`w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        errors.password ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                  {mode === 'create' && (
                    <p className="mt-1 text-xs text-gray-500">Password must be at least 6 characters</p>
                  )}
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Shield className="inline h-4 w-4 mr-1" />
                    Role *
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.role ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="technician">Technician</option>
                    <option value="admin">Admin</option>
                  </select>
                  {errors.role && (
                    <p className="mt-1 text-sm text-red-600">{errors.role}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="inline h-4 w-4 mr-1" />
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>

                {/* Is Active (edit mode only) */}
                {mode === 'edit' && (
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleChange}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                )}

                {formData.role === 'technician' && (
                  <div className="border-t border-gray-200 pt-4 space-y-4">
                    <h4 className="text-sm font-semibold text-gray-900">Auto-Schedule Profile</h4>

                    <div>
                      <span className="block text-sm font-medium text-gray-700 mb-2">Capabilities</span>
                      <div className="flex flex-wrap gap-3">
                        {[
                          { id: 'inspection', label: 'Inspections' },
                          { id: 'service', label: 'Service' },
                        ].map(({ id, label }) => (
                          <label key={id} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.capabilities.includes(id)}
                              onChange={() => handleCapabilityToggle(id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">{label}</span>
                          </label>
                        ))}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">Newer techs typically start with inspections only.</p>
                    </div>

                    <div>
                      <span className="block text-sm font-medium text-gray-700 mb-2">Generator cooling types</span>
                      <div className="flex flex-wrap gap-3">
                        {[
                          { id: 'air', label: 'Air-cooled' },
                          { id: 'water', label: 'Water-cooled' },
                        ].map(({ id, label }) => (
                          <label key={id} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.coolingCapabilities.includes(id)}
                              onChange={() => handleCoolingToggle(id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">{label}</span>
                          </label>
                        ))}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Clients whose cooling type isn&apos;t recorded yet can go to anyone.
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        For time off and a full scheduling view, use{' '}
                        <Link to="/tech-scheduling" className="text-blue-600 hover:text-blue-800" onClick={onClose}>
                          Technician Scheduling
                        </Link>.
                      </p>
                    </div>

                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="includeInAutoSchedule"
                          checked={formData.includeInAutoSchedule}
                          onChange={handleChange}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Include in auto-schedule runs</span>
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Daily work budget override (minutes)
                      </label>
                      <input
                        type="number"
                        name="dailyCapacityMinutes"
                        min="30"
                        max="1440"
                        step="15"
                        value={formData.dailyCapacityMinutes}
                        onChange={handleChange}
                        placeholder="Use global default"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">Drivable + on-site minutes this tech handles per day. Leave blank to use the global default from Settings.</p>
                    </div>

                    {mode === 'edit' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Home address
                        </label>
                        <input
                          type="text"
                          name="homeAddress"
                          value={formData.homeAddress}
                          onChange={handleChange}
                          placeholder="456 Oak Ave, Jackson, MS"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          {user?.homeLatitude != null && user?.homeLongitude != null
                            ? `Geocoded ✓ (${Number(user.homeLatitude).toFixed(4)}, ${Number(user.homeLongitude).toFixed(4)}).`
                            : 'Saved addresses are geocoded automatically.'}
                        </p>
                        <label className="mt-3 flex items-center">
                          <input
                            type="checkbox"
                            name="startFromHomeAddress"
                            checked={formData.startFromHomeAddress}
                            disabled={!formData.homeAddress.trim()}
                            onChange={handleChange}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700">Start route from home address</span>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Saving...' : mode === 'create' ? 'Create User' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}







